import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Row, Col, Offcanvas, Card, ListGroup, OverlayTrigger, Tooltip, Navbar, NavbarBrand } from 'react-bootstrap';
import { useAuth } from './auth/AuthContext';
import { CompetitorInfoLine } from './CompetitorInfoLine'; // Reusing from LiveTimetable
import { createRealtimeMapCoalescer, subscribeToRealtime } from './realtimeHub';
import { buildBroeCrewUrl, toDotNetDateTicks } from '../utils/broe';
import '../css/livetimetable.css';

const labelClass = { color: "#555", fontSize: 14, fontWeight: 700, marginBottom: 7 };

export function Racing() {
    const [races, setRaces] = useState([]);
    const [events, setEvents] = useState([]);
    const [entries, setEntries] = useState([]);
    const [victorLudorum, setVictorLudorum] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showCanvas, setShowCanvas] = useState(false);
    const [canvasData, setCanvasData] = useState({ race: null, competitors: [] });
    const [onCourseTickerText, setOnCourseTickerText] = useState('Course Empty');
    const [hideCollected, setHideCollected] = useState(false);
    const [broeDateTicks, setBroeDateTicks] = useState('');
    const [recentlyCollected, setRecentlyCollected] = useState(() => new Set());
    const hideTimersRef = useRef(new Map());
    const isMountedRef = useRef(false);

    const { isAdmin, isRegistration, getToken, loading: authLoading } = useAuth();

    const fetchData = useCallback(async (url) => {
        const token = getToken();
        const response = await fetch(url, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) {
            throw new Error(`HTTP error: ${response.status}`);
        }
        return response.json();
    }, [getToken]);

    const loadVictorLudorum = useCallback(async () => {
        try {
            const data = await fetchData('/api/timetable/CalculateVictorLudorum');
            if (isMountedRef.current) {
                setVictorLudorum(Array.isArray(data) ? data : []);
            }
        } catch (err) {
            console.error("Failed to load Victor Ludorum", err);
        }
    }, [fetchData]);

    const loadAllData = useCallback(async () => {
        try {
            const [racesData, eventsData, entriesData, meetingData] = await Promise.all([
                fetchData('/api/timetable'),
                fetchData('/api/event'),
                fetchData('/api/meeting/entries'),
                fetchData('/api/meeting/current').catch(() => null)
            ]);
            setRaces(racesData);
            setEvents(eventsData);
            setEntries(entriesData);
            setBroeDateTicks(toDotNetDateTicks(meetingData?.meetingDate || meetingData?.competitionDate));
            await loadVictorLudorum();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [fetchData, loadVictorLudorum]);

    const loadOnCourseTicker = useCallback(async () => {
        try {
            const token = getToken();
            const response = await fetch('/api/race/oncourse', {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
            const payload = await response.json();
            if (!isMountedRef.current) return;

            const items = (Array.isArray(payload) ? payload : [])
                .map(item => String(item?.text || '').trim())
                .filter(Boolean);

            setOnCourseTickerText(items.length ? items.join('  •  ') : 'Course Empty');
        } catch (err) {
            console.log(err.message);
            if (isMountedRef.current) {
                setOnCourseTickerText('Course Empty');
            }
        }
    }, [getToken]);

    useEffect(() => {
        isMountedRef.current = true;
        if (authLoading) {
            return undefined;
        }

        if (!isAdmin && !isRegistration) {
            setError("You are not authorized to view this page.");
            setLoading(false);
            return undefined;
        }

        loadAllData();
        loadOnCourseTicker();

        const receiveRaceResultCoalescer = createRealtimeMapCoalescer((events) => {
            setRaces(prevRaces => {
                const _races = [...prevRaces];
                events.forEach(({ competitionId, raceid, winnerid, verdict, timetaken, nextrace }) => {
                    const raceUpdate = _races.find(race => race.raceId === raceid && race.competitionId === competitionId);
                    if (!raceUpdate) return;

                    let winnerInfo;
                    raceUpdate.verdict = verdict;
                    raceUpdate.winningTime = timetaken;
                    raceUpdate.laneA.winner = winnerid === 'a';
                    raceUpdate.laneB.winner = winnerid === 'b';
                    winnerInfo = winnerid === 'a' ? raceUpdate.laneA : raceUpdate.laneB;

                    if (raceUpdate.laneA.winner || raceUpdate.laneB.winner) {
                        raceUpdate.status = 3;
                    }

                    if ((raceUpdate.round !== 'F' || nextrace?.raceId != null) && winnerInfo) {
                        const nextRound = _races.find(race => race.raceId === nextrace?.raceId);
                        if (nextRound) {
                            if (nextRound.wora === raceUpdate.raceId) {
                                nextRound.laneA.title = winnerInfo.title;
                                nextRound.laneA.titleMobile = winnerInfo.titleMobile;
                                nextRound.laneA.titleAbbreviated = winnerInfo.titleAbbreviated;
                                nextRound.laneA.titleFull = winnerInfo.titleFull;
                                nextRound.laneA.competitorNo = winnerInfo.competitorNo;
                                nextRound.laneA.suffix = winnerInfo.suffix;
                                nextRound.laneA.titleCode = winnerInfo.titleCode;
                            }
                            if (nextRound.worb === raceUpdate.raceId) {
                                nextRound.laneB.title = winnerInfo.title;
                                nextRound.laneB.titleMobile = winnerInfo.titleMobile;
                                nextRound.laneB.titleAbbreviated = winnerInfo.titleAbbreviated;
                                nextRound.laneB.titleFull = winnerInfo.titleFull;
                                nextRound.laneB.competitorNo = winnerInfo.competitorNo;
                                nextRound.laneB.suffix = winnerInfo.suffix;
                                nextRound.laneB.titleCode = winnerInfo.titleCode;
                            }
                        }
                    }
                });
                return _races;
            });
        }, (event) => `${event.competitionId}:${event.raceid}`);

        const raceStatusUpdatedCoalescer = createRealtimeMapCoalescer((events) => {
            setRaces(prevRaces => {
                const _races = [...prevRaces];
                events.forEach(({ competitionId, raceId, statusId }) => {
                    const raceUpdate = _races.find(race => race.raceId === raceId && race.competitionId === competitionId);
                    if (raceUpdate) {
                        raceUpdate.status = statusId;
                    }
                });
                return _races;
            });
        }, (event) => `${event.competitionId}:${event.raceId}`);

        const raceMovedCoalescer = createRealtimeMapCoalescer((events) => {
            setRaces(prevRaces => {
                const _races = [...prevRaces];
                events.forEach(({ competitionId, raceid, newraceid }) => {
                    if (_races.length === 0 || _races[0].competitionId !== competitionId) return;

                    const newRaceId = parseInt(newraceid, 10);
                    const raceA = { ..._races.find(race => race.raceId === raceid) };
                    const raceB = { ..._races.find(race => race.raceId === newRaceId) };
                    const destinationRace = _races.find(race => race.raceId === newRaceId);
                    const sourceRace = _races.find(race => race.raceId === raceid);
                    if (!destinationRace || !sourceRace || !raceA || !raceB) return;

                    destinationRace.raceNo = raceA.raceNo;
                    destinationRace.laneA.title = raceA.laneA.title;
                    destinationRace.laneA.titleShort = raceA.laneA.titleShort;
                    destinationRace.laneA.suffix = raceA.laneA.suffix;
                    destinationRace.laneB.title = raceA.laneB.title;
                    destinationRace.laneB.titleShort = raceA.laneB.titleShort;
                    destinationRace.laneB.suffix = raceA.laneB.suffix;
                    destinationRace.event.id = raceA.event.id;
                    destinationRace.event.title = raceA.event.title;
                    destinationRace.event.titleShort = raceA.event.titleShort;
                    destinationRace.round = raceA.round;
                    destinationRace.divsion = raceA.division;
                    destinationRace.nextRaceId = raceA.nextRaceId;

                    sourceRace.raceNo = raceB.raceNo;
                    sourceRace.laneA.title = '';
                    sourceRace.laneB.title = '';
                    sourceRace.laneA.suffix = '';
                    sourceRace.laneB.suffix = '';
                    sourceRace.event.id = -1;
                    sourceRace.event.title = '';
                    sourceRace.event.titleShort = '';
                    sourceRace.round = '';
                    sourceRace.nextRaceId = '';
                });
                return _races;
            });
        }, (event) => `${event.competitionId}:${event.raceid}`);

        const unsubscribe = subscribeToRealtime({
                onReconnected: () => {
                    loadAllData();
                    loadOnCourseTicker();
                },
                onReceiveRaceResult: (competitionId, raceid, winnerid, verdict, timetaken, nextrace) => {
                    receiveRaceResultCoalescer.push({ competitionId, raceid, winnerid, verdict, timetaken, nextrace });
                    loadOnCourseTicker();
                    loadVictorLudorum();
                },
                onRaceStatusUpdated: (competitionId, raceId, statusId) => {
                    raceStatusUpdatedCoalescer.push({ competitionId, raceId, statusId });
                    loadOnCourseTicker();
                },
                onRaceMoved: (competitionId, raceid, newraceid) => {
                    raceMovedCoalescer.push({ competitionId, raceid, newraceid });
                    loadOnCourseTicker();
                }
            });

        const hideTimers = hideTimersRef.current;

        return () => {
            isMountedRef.current = false;
            receiveRaceResultCoalescer.dispose();
            raceStatusUpdatedCoalescer.dispose();
            raceMovedCoalescer.dispose();
            unsubscribe();
            hideTimers.forEach(timer => clearTimeout(timer));
            hideTimers.clear();
        };
    }, [authLoading, loadAllData, loadOnCourseTicker, loadVictorLudorum, isAdmin, isRegistration]);

    const handleShowCanvas = async (race) => {
        let crewId = null;
        if (race.laneA.winner) {
            crewId = race.laneA.id;
        } else if (race.laneB.winner) {
            crewId = race.laneB.id;
        }

        if (!crewId) return;

        try {
            const competitors = await fetchData(`/api/competitor/${crewId}`);
            setCanvasData({ race, competitors });
            setShowCanvas(true);
        } catch (err) {
            console.error("Failed to fetch competitors", err);
            setError("Failed to load crew details.");
        }
    };

    const handleTogglePrizeCollected = async (eventId, currentlyCollected) => {
        try {
            const token = getToken();
            await fetch(`/api/event/toggle-prize-collected/${eventId}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const existingTimer = hideTimersRef.current.get(eventId);
            if (existingTimer) {
                clearTimeout(existingTimer);
                hideTimersRef.current.delete(eventId);
            }

            if (!currentlyCollected) {
                // Just ticked: keep visible for 2 minutes before hiding
                setRecentlyCollected(prev => {
                    const next = new Set(prev);
                    next.add(eventId);
                    return next;
                });
                hideTimersRef.current.set(eventId, setTimeout(() => {
                    hideTimersRef.current.delete(eventId);
                    if (!isMountedRef.current) return;
                    setRecentlyCollected(prev => {
                        const next = new Set(prev);
                        next.delete(eventId);
                        return next;
                    });
                }, 2 * 60 * 1000));
            } else {
                // Unticked: cancel any pending hide
                setRecentlyCollected(prev => {
                    const next = new Set(prev);
                    next.delete(eventId);
                    return next;
                });
            }

            loadAllData();
        } catch (err) {
            console.error("Failed to toggle prize collected status", err);
        }
    };

    const handleToggleLicencesInspected = async (eventId) => {
        try {
            const token = getToken();
            await fetch(`/api/event/toggle-licences-inspected/${eventId}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            loadAllData();
        } catch (err) {
            console.error("Failed to toggle licences inspected status", err);
        }
    };

    const handleCloseCanvas = () => setShowCanvas(false);

    const prizeEligibleWins = races
        .filter(r => {
            const winningLane = r.laneA.winner ? r.laneA : r.laneB.winner ? r.laneB : null;
            if (r.round !== 'F' || !winningLane || !winningLane.id || winningLane.id <= 0) {
                return false;
            }

            const eventDetails = events.find(e => e.id === r.event.id);
            if (!eventDetails) {
                return false;
            }

            // Check if there is at least one crew in the entire event with status 1
            const eventEntries = entries.filter(e => e.eventName === r.event.titleShort);
            const hasActiveCrews = eventEntries.some(e => e.status === 1);

            if (!hasActiveCrews) {
                return false;
            }

            return true;
        });

    const unrunFinals = races
        .filter(r => r.round === 'F' && !r.laneA.winner && !r.laneB.winner);

    const prizesForEvent = (eventId) => {
        const eventDetails = events.find(e => e.id === eventId);
        if (!eventDetails) return 1;
        return Number(eventDetails.seats || 0) + (eventDetails.coxed ? 1 : 0);
    };

    const prizesToCollect = prizeEligibleWins
        .filter(r => !events.find(e => e.id === r.event.id)?.prizesCollected)
        .concat(unrunFinals)
        .reduce((total, r) => total + prizesForEvent(r.event.id), 0);

    const finalWinners = prizeEligibleWins
        .filter(r => {
            const eventDetails = events.find(e => e.id === r.event.id);
            if (hideCollected && eventDetails.prizesCollected && !recentlyCollected.has(r.event.id)) {
                return false;
            }
            return true;
        })
        .sort((a, b) => Number(b.raceNo) - Number(a.raceNo));

    const finalsToGo = unrunFinals.length;
    const racesToGo = races.filter(r => !r.laneA.winner && !r.laneB.winner).length;

    const sortedClubPoints = victorLudorum
        .map(item => ({
            club: item.name || item.shortName || item.code,
            points: Number(item.points || 0),
            contributors: Array.isArray(item.contributors) ? item.contributors : [],
            potentialPoints: Number(item.potentialPoints || 0),
            totalPotential: Number(item.totalPotential ?? (Number(item.points || 0) + Number(item.potentialPoints || 0)))
        }))
        .slice(0, 9);


    const laneAWins = races.filter(r => r.laneA.winner).length;
    const laneBWins = races.filter(r => r.laneB.winner).length;
    const totalWins = laneAWins + laneBWins;
    const laneAPercentage = totalWins > 0 ? ((laneAWins / totalWins) * 100).toFixed(0) : 0;
    const laneBPercentage = totalWins > 0 ? ((laneBWins / totalWins) * 100).toFixed(0) : 0;

    const tickerDisplayText = onCourseTickerText === 'Course Empty'
        ? 'Course Empty'
        : `On Course Now: ${onCourseTickerText}`;

    if (loading) {
        return <p><em>Loading...</em></p>;
    }

    if (error) {
        return <div className="alert alert-danger">{error}</div>;
    }

    return (
        <div className="pb-5 mb-5">
            <Container fluid>
            <h1>Racing Dashboard</h1>
            <Row>
                <Col md={{ span: 8, order: 1 }} xs={{ order: 2 }}>
                    <div style={{ border: '1px solid #bbb', borderRadius: 6, background: '#fff', padding: 10, marginBottom: 16 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={labelClass}>
                                <div>Final Winners</div>
                                <div>({prizesToCollect} prize{prizesToCollect === 1 ? '' : 's'} to collect)</div>
                            </div>
                            {(isAdmin || isRegistration) && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <span style={{ fontSize: 12, color: '#888', marginBottom: 7 }}>
                                        C = Prizes Collected<span className="d-none d-md-inline"> | </span><br className="d-md-none" />L = Licences Inspected
                                    </span>
                                    <label style={{ fontSize: 12, color: '#555', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 7, cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={hideCollected}
                                            onChange={(e) => setHideCollected(e.target.checked)}
                                        />
                                        Hide collected
                                    </label>
                                </div>
                            )}
                        </div>
                        <div style={{ borderBottom: '1px solid #ddd', margin: '4px 0 8px 0', width: '100%' }} />
                        <div style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                            {finalWinners.map(race => {
                                const eventDetails = events.find(e => e.id === race.event.id);
                                const winningLane = race.laneA.winner ? race.laneA : race.laneB;
                                const club = String(winningLane.title || '').trim();
                                const competitorNo = String(winningLane.competitorNo || '').trim();
                                const crew = String(winningLane.suffix || '').trim();
                                const clubWithNumber = competitorNo && !club.startsWith(`${competitorNo} - `)
                                    ? `${competitorNo} - ${club}`
                                    : club;
                                const winnerLabel = crew ? `${clubWithNumber} (${crew})` : clubWithNumber;
                                const broeUrl = buildBroeCrewUrl(winningLane.id, broeDateTicks);
                                return (
                                    <Card key={race.raceId} className="mb-2">
                                        <Row className="align-items-center g-0" style={{ fontSize: 12, padding: '0.5rem' }}>
                                            <Col xs={3} md={1}>
                                                {(isAdmin || isRegistration) && (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                                        <label title="Prizes Collected" style={{ display: 'flex', alignItems: 'center', gap: 3, marginBottom: 0, cursor: 'pointer' }}>
                                                            <input
                                                                type="checkbox"
                                                                aria-label="Prizes Collected"
                                                                checked={eventDetails?.prizesCollected || false}
                                                                onChange={() => handleTogglePrizeCollected(race.event.id, eventDetails?.prizesCollected || false)}
                                                            />
                                                            <span style={{ fontSize: 11, color: '#888', fontWeight: 600 }}>C</span>
                                                        </label>
                                                        <label title="Licences Inspected" style={{ display: 'flex', alignItems: 'center', gap: 3, marginBottom: 0, cursor: 'pointer' }}>
                                                            <input
                                                                type="checkbox"
                                                                aria-label="Licences Inspected"
                                                                checked={eventDetails?.licencesInspected || false}
                                                                onChange={() => handleToggleLicencesInspected(race.event.id)}
                                                            />
                                                            <span style={{ fontSize: 11, color: '#888', fontWeight: 600 }}>L</span>
                                                        </label>
                                                    </div>
                                                )}
                                            </Col>
                                            <Col md={2} className="d-none d-md-block" onClick={() => handleShowCanvas(race)} style={{ cursor: 'pointer' }}>
                                                <strong>Race {race.raceNo}</strong>
                                            </Col>
                                            <Col xs={5} md={5} onClick={() => handleShowCanvas(race)} style={{ cursor: 'pointer' }}>
                                                {race.event.title}
                                            </Col>
                                            <Col xs={4} md={4} className="text-end" onClick={() => handleShowCanvas(race)} style={{ cursor: 'pointer' }}>
                                                {winnerLabel}
                                                {broeUrl && (
                                                    <a
                                                        href={broeUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        title="Open in BROE"
                                                        aria-label={`Open ${winnerLabel} in BROE`}
                                                        onClick={(e) => e.stopPropagation()}
                                                        style={{ color: '#0d6efd', textDecoration: 'none', marginLeft: 6, verticalAlign: 'baseline' }}
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                                                            <path fillRule="evenodd" d="M14 3.5a.5.5 0 0 0-.5-.5h-4a.5.5 0 0 0 0 1h2.793L7.146 9.146a.5.5 0 1 0 .708.708L13 4.707V7.5a.5.5 0 0 0 1 0z" />
                                                            <path fillRule="evenodd" d="M2.5 1A1.5 1.5 0 0 0 1 2.5v11A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-4a.5.5 0 0 0-1 0v4a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5h4a.5.5 0 0 0 0-1z" />
                                                        </svg>
                                                    </a>
                                                )}
                                            </Col>
                                        </Row>
                                    </Card>
                                );
                            })}
                        </div>
                    </div>
                </Col>
                <Col md={{ span: 4, order: 2 }} xs={{ order: 1 }}>
                    <div style={{ border: '1px solid #bbb', borderRadius: 6, background: '#fff', padding: 10, marginBottom: 16 }}>
                        <div style={labelClass}>Lane Wins ({racesToGo}TG)</div>
                        <div style={{ borderBottom: '1px solid #ddd', margin: '4px 0 8px 0', width: '100%' }} />
                        <div style={{ padding: '4px 0' }}>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                                <div style={{ width: 140, fontSize: 13, fontWeight: 600, flexShrink: 0 }}>Middlesex (Enc)</div>
                                <div style={{ flex: 1, background: '#e9ecef', borderRadius: 4, overflow: 'hidden', height: 22 }}>
                                    <div style={{ width: `${laneAPercentage}%`, background: '#0d6efd', height: '100%', borderRadius: 4, transition: 'width 0.4s ease', minWidth: laneAWins > 0 ? 2 : 0 }} />
                                </div>
                                <div style={{ width: 60, textAlign: 'right', fontSize: 13, flexShrink: 0 }}>{laneAWins} ({laneAPercentage}%)</div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{ width: 140, fontSize: 13, fontWeight: 600, flexShrink: 0 }}>Surrey</div>
                                <div style={{ flex: 1, background: '#e9ecef', borderRadius: 4, overflow: 'hidden', height: 22 }}>
                                    <div style={{ width: `${laneBPercentage}%`, background: '#dc3545', height: '100%', borderRadius: 4, transition: 'width 0.4s ease', minWidth: laneBWins > 0 ? 2 : 0 }} />
                                </div>
                                <div style={{ width: 60, textAlign: 'right', fontSize: 13, flexShrink: 0 }}>{laneBWins} ({laneBPercentage}%)</div>
                            </div>
                        </div>
                    </div>
                    <div style={{ border: '1px solid #bbb', borderRadius: 6, background: '#fff', padding: 10, marginBottom: 16 }}>
                        <div style={labelClass}>Victor Ludorum ({finalsToGo}TG)</div>
                        <div style={{ borderBottom: '1px solid #ddd', margin: '4px 0 8px 0', width: '100%' }} />
                        <div style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                            <ListGroup>
                                {sortedClubPoints.map(({ club, points, contributors, potentialPoints, totalPotential }) => {
                                    return (
                                        <OverlayTrigger
                                            key={club}
                                            placement="left"
                                            overlay={
                                                <Tooltip id={`tooltip-${club}`}>
                                                    <div style={{ textAlign: 'left' }}>
                                                        <strong>Contributing Crews:</strong>
                                                        <ul style={{ paddingLeft: '20px', marginBottom: '0' }}>
                                                            {contributors.map((c, i) => <li key={i}>{c}</li>)}
                                                        </ul>
                                                    </div>
                                                </Tooltip>
                                            }
                                        >
                                            <ListGroup.Item className="d-flex justify-content-between align-items-start" style={{ fontSize: 14, padding: '0.5rem 1rem', border: '0', borderBottom: '1px solid rgba(0,0,0,.125)' }}>
                                                <div className="ms-2 me-auto">
                                                    <div>{club}</div>
                                                </div>
                                                <span className="badge bg-success rounded-pill">
                                                    {points.toFixed(0)}
                                                    {potentialPoints > 0 && ` / ${totalPotential.toFixed(0)}`}
                                                </span>
                                            </ListGroup.Item>
                                        </OverlayTrigger>
                                    );
                                })}
                            </ListGroup>
                        </div>
                    </div>
                </Col>
            </Row>

            <Offcanvas show={showCanvas} onHide={handleCloseCanvas} placement="end">
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>Crew Details</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    {canvasData.race && (
                        <>
                            <h5>{canvasData.race.event.title}</h5>
                            <p>
                                <strong>Winner:</strong> {canvasData.race.laneA.winner ? canvasData.race.laneA.title : canvasData.race.laneB.title}
                            </p>
                            <hr />
                        </>
                    )}
                    <ListGroup variant="flush">
                        {canvasData.competitors.map(competitor => (
                            <CompetitorInfoLine key={competitor.id} value={competitor} />
                        ))}
                    </ListGroup>
                </Offcanvas.Body>
            </Offcanvas>
            </Container>
            <div className="fixed-bottom">
                <Navbar bg="light" variant="light">
                    <Container>
                        <NavbarBrand></NavbarBrand>
                        <Navbar.Text className='courseTickerWrap'>
                            <div className='courseTickerTrack'>
                                <span className={`courseTickerText ${onCourseTickerText === 'Course Empty' ? 'is-empty' : ''}`}>{tickerDisplayText}</span>
                            </div>
                        </Navbar.Text>
                    </Container>
                </Navbar>
            </div>
            <div className="divFooter container-fluid">
                <div className='row'>
                    <div className='col-9'></div>
                    <div className='col-3'></div>
                </div>
            </div>
        </div>
    );
}
